import { parseContentfulLink, type ContentfulLink } from "@/utils/contentful";
import { isRecord } from "@/utils/unknown";

export type MarketplaceExtensionFields = {
  name?: string;
  available?: boolean;
  certified?: boolean;
  creator?: ContentfulLink<"Entry">;
  categories: ContentfulLink<"Entry">[];
  screenshots: ContentfulLink<"Asset">[];
  content?: ContentfulLink<"Asset">;
};

export type MarketplaceCreatorFields = {
  fullname?: string;
  username?: string;
};

export type MarketplaceCategoryFields = {
  name?: string;
};

const links = <TLinkType extends "Entry" | "Asset">(
  value: unknown,
  linkType: TLinkType
): ContentfulLink<TLinkType>[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const link = parseContentfulLink(item, linkType);
    return link ? [link] : [];
  });
};

export function parseMarketplaceExtensionFields(
  value: unknown
): MarketplaceExtensionFields | null {
  if (!isRecord(value)) return null;

  const creator = parseContentfulLink(value.creator, "Entry");
  const content = parseContentfulLink(value.content, "Asset");
  const screenshot = parseContentfulLink(value.screenshot, "Asset");

  return {
    ...(typeof value.name === "string" ? { name: value.name } : {}),
    ...(typeof value.available === "boolean"
      ? { available: value.available }
      : {}),
    ...(typeof value.certified === "boolean"
      ? { certified: value.certified }
      : {}),
    ...(creator ? { creator } : {}),
    categories: links(value.categories, "Entry"),
    screenshots: [
      ...links(value.screenshots, "Asset"),
      ...(screenshot ? [screenshot] : []),
    ],
    ...(content ? { content } : {}),
  };
}

export function parseMarketplaceCreatorFields(
  value: unknown
): MarketplaceCreatorFields | null {
  if (!isRecord(value)) return null;
  return {
    ...(typeof value.fullname === "string" ? { fullname: value.fullname } : {}),
    ...(typeof value.username === "string" ? { username: value.username } : {}),
  };
}

export function parseMarketplaceCategoryFields(
  value: unknown
): MarketplaceCategoryFields | null {
  if (!isRecord(value)) return null;
  return {
    ...(typeof value.name === "string" ? { name: value.name } : {}),
  };
}
